// ENG-5672: idle-gated, server-pinned Claude Code auto-updater.
// ENG-5798: multi-install drift handling — detect coexisting claude binaries,
//           short-circuit on native autoupdater hosts, verify against absolute
//           install path, hard-error after N consecutive verify mismatches.
//
// Hosts install @anthropic-ai/claude-code once at bootstrap (host-bootstrap.ts)
// and never refresh it, so a long-lived host runs a stale Claude Code forever.
// This module brings a host to its *desired* version, which the server pins via
// /host/refresh (per-host override, else fleet default, else 'latest').
//
// Safety properties (this runs npm install -g on live prod hosts):
//   • Idle-gated — we never install while a `claude --name agt-*` agent process
//     is running on the host (a running process holds the old binary anyway;
//     replacing it under a live session is pointless and risky). Busy → defer
//     to a later cycle.
//   • No-op when already on target — a concrete target equal to the installed
//     version short-circuits; a dist-tag ('latest') is resolved to a concrete
//     version via `npm view` and compared, so we don't reinstall every cycle.
//   • Host-global throttle — at most one check per interval (default 6h), so the
//     manager's ~10-30s poll doesn't shell out to npm/ps constantly.
//   • Log-and-continue — every failure is logged and swallowed; an error here
//     must never break the manager poll cycle.
//   • Multi-install awareness (ENG-5798) — on hosts where Anthropic's native
//     autoupdater owns /usr/bin/claude, this updater short-circuits cleanly
//     instead of fighting it. On hosts where multiple binaries coexist (e.g. a
//     brew cask alongside the npm global), we log every path+version once so
//     drift is visible in manager.log without spamming every cycle.
//
// The *installed* version is reported separately as hosts.framework_version
// (the claude-code framework adapter's getVersion() == `claude --version`), so
// this module does not report it itself.

import { execFile, execFileSync } from 'node:child_process';
import { existsSync, readFileSync, realpathSync } from 'node:fs';
import { homedir } from 'node:os';
import { promisify } from 'node:util';
import { effectiveClaudeJsonPath } from './claude-config-location.js';
import { parsePsRows, CLAUDE_AGENT_PROCESS_PATTERN } from './orphan-channel-mcp-reaper.js';

const execFileAsync = promisify(execFile);

const NPM_PKG = '@anthropic-ai/claude-code';

/** Default host-global check interval. Overridable via env for tuning. */
const DEFAULT_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6h

/**
 * ENG-5798: After this many consecutive verify mismatches against the same
 * target version, emit a distinct one-shot error code that the
 * synthetic-probe / CloudWatch alarm pipeline can latch onto, instead of
 * silently re-failing forever. Overridable via env for testing.
 */
const DEFAULT_VERIFY_MISMATCH_THRESHOLD = 5;

function intervalMs(): number {
  const raw = Number.parseInt(process.env['AGT_CLAUDE_UPDATE_INTERVAL_MS'] ?? '', 10);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_INTERVAL_MS;
}

function verifyMismatchThreshold(): number {
  const raw = Number.parseInt(process.env['AGT_CLAUDE_UPDATE_VERIFY_THRESHOLD'] ?? '', 10);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_VERIFY_MISMATCH_THRESHOLD;
}

/** Extract a version string (leading semver) from `claude --version` / npm output. */
export function parseVersion(output: string): string | null {
  const m = output.match(/\d+\.\d+\.\d+[A-Za-z0-9.+-]*/);
  return m ? m[0] : null;
}

/** A target spec is "concrete" when it already names an exact version. */
export function isConcreteVersion(spec: string): boolean {
  return /^\d+\.\d+\.\d+[A-Za-z0-9.+-]*$/.test(spec.trim());
}

/**
 * Well-known absolute paths where a `claude` binary may live on managed hosts.
 * Probed alongside the PATH-resolved binary so we can see all install slots,
 * even ones not on the manager systemd unit's PATH.
 */
export const WELL_KNOWN_CLAUDE_PATHS: readonly string[] = [
  '/usr/bin/claude', // Anthropic native autoupdater (Linux deb/rpm)
  '/usr/local/bin/claude', // npm global on macOS Intel + Linux without nvm
  '/opt/homebrew/bin/claude', // Homebrew on macOS Apple Silicon
  '/home/linuxbrew/.linuxbrew/bin/claude', // Linuxbrew (used on agt-aws-* hosts)
];

export interface ClaudeCodeUpdaterDeps {
  /** Server-pinned target: a concrete version or an npm dist-tag like 'latest'. */
  desiredVersion: string;
  /** Currently-installed version, if the caller already knows it (e.g. the
   *  manager's cachedFrameworkVersion). When omitted we run `claude --version`. */
  currentVersion?: string | null;
  log: (msg: string) => void;
  /** Override "now" for tests. */
  now?: () => number;
  /** Run a command; injected in tests. Defaults to execFile. */
  runCommand?: (cmd: string, args: string[]) => Promise<{ stdout: string; stderr: string }>;
  /** Raw `ps -eo pid,ppid,args` snapshot; injected in tests. */
  psSnapshot?: () => string;
  /** Sync existence probe for binary detection; injected in tests. */
  fileExists?: (path: string) => boolean;
  /** Read ~/.claude.json contents (raw); returns null when absent. Injected in tests. */
  readClaudeJson?: (path: string) => string | null;
  /** Resolve the user's home directory; injected in tests. */
  homeDir?: () => string;
  /** Resolve symlinks to a real path (throws when absent); injected in tests. */
  realPath?: (path: string) => string;
}

export type UpdaterOutcome =
  | { action: 'throttled' }
  | { action: 'up_to_date'; version: string }
  | { action: 'deferred_busy'; target: string }
  | { action: 'skipped_native_install'; target: string }
  | { action: 'updated'; from: string | null; to: string }
  | { action: 'failed'; reason: string };

// ── Host-global state (module-level; one updater per host process) ──────────
let lastCheckAt = 0;
/** Per-process one-shot guard for the multi-install warn line. */
let multiInstallWarned = false;
/**
 * Sticky counter for consecutive `post-install verify mismatch` against the
 * same target version. Reset on success, on target change, or after the
 * threshold error has been emitted (so we emit it exactly once per stuck run).
 */
let verifyMismatchState: { target: string; count: number; alarmed: boolean } | null = null;

/** Test helper — reset module state. Not for production use. */
export function _resetClaudeCodeUpdaterState(): void {
  lastCheckAt = 0;
  multiInstallWarned = false;
  verifyMismatchState = null;
}

function defaultPsSnapshot(): string {
  return execFileSync('ps', ['-eo', 'pid,ppid,args'], { encoding: 'utf-8', maxBuffer: 16 * 1024 * 1024 });
}

function defaultReadClaudeJson(path: string): string | null {
  try {
    return readFileSync(path, 'utf-8');
  } catch {
    return null;
  }
}

/** True when at least one `claude --name agt-*` agent process is live. */
export function hostHasLiveAgent(psOutput: string): boolean {
  return parsePsRows(psOutput).some((r) => CLAUDE_AGENT_PROCESS_PATTERN.test(r.args));
}

/**
 * ENG-5798: returns true when ~/.claude.json reports `installMethod: "native"`,
 * meaning Anthropic's native autoupdater owns the on-disk binary. In that case
 * the brew/npm update path is actively harmful — it installs a second binary
 * the host's PATH never resolves to, and verify reads from yet a third.
 */
export function readsAsNativeInstall(raw: string | null): boolean {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { installMethod?: unknown };
    return parsed.installMethod === 'native';
  } catch {
    return false;
  }
}

/**
 * ENG-10620: true when `realPath` is a binary owned by Claude Code's native
 * installer, which keeps every version under `…/claude/versions/<semver>`
 * (e.g. `~/.local/share/claude/versions/2.1.274`, symlinked from
 * `~/.local/bin/claude`). An npm global resolves into
 * `…/node_modules/@anthropic-ai/claude-code/` instead.
 */
export function isNativeInstallPath(realPath: string): boolean {
  return /\/claude\/versions\/\d+\.\d+\.\d+[^/]*$/.test(realPath);
}

/**
 * ENG-10620: decide native ownership from the binary this host ACTUALLY runs,
 * not from `~/.claude.json`.
 *
 * `installMethod` is written by whichever `claude` last started, not by the
 * one the manager launches. On agt-aws-1 an operator shell's PATH resolved a
 * native `~/.local/bin/claude` while agents ran the npm global at
 * `/usr/bin/claude`; that one manual run flipped `installMethod` to `native`
 * and this updater stopped converging the host to its pin for good, while the
 * binary it would have updated was never native at all.
 *
 * Resolution mirrors how agent sessions pick their binary (resolveClaudeBinary:
 * CLAUDE_PATH, then PATH). The config key remains only as a fallback for a
 * host where no binary can be resolved.
 */
async function resolveNativeOwnership(
  run: (cmd: string, args: string[]) => Promise<{ stdout: string; stderr: string }>,
  realPath: (path: string) => string,
  claudeJson: string | null,
): Promise<{ native: boolean; binary: string | null; configSaysNative: boolean }> {
  const configSaysNative = readsAsNativeInstall(claudeJson);
  let resolved: string | null = null;
  const override = process.env['CLAUDE_PATH'];
  if (override) {
    resolved = override;
  } else {
    try {
      const { stdout } = await run('which', ['claude']);
      resolved = stdout.trim().split('\n')[0]?.trim() || null;
    } catch {
      resolved = null;
    }
  }
  if (resolved) {
    try {
      const real = realPath(resolved);
      return { native: isNativeInstallPath(real), binary: real, configSaysNative };
    } catch {
      /* unresolvable — fall back to the config key */
    }
  }
  return { native: configSaysNative, binary: null, configSaysNative };
}

/**
 * Resolve the absolute filesystem path that `npm install -g <pkg>` will write
 * the `claude` binary to. Returns null when `npm prefix -g` fails or is empty,
 * in which case the caller should fall back to PATH-based verify.
 *
 * We deliberately use `npm prefix -g` rather than `which claude` because the
 * latter resolves through PATH, which on agt-aws-* hosts can land on
 * /usr/bin/claude (the native autoupdater slot) — exactly the misalignment
 * ENG-5798 is fixing.
 */
async function resolveInstallTargetPath(
  run: (cmd: string, args: string[]) => Promise<{ stdout: string; stderr: string }>,
): Promise<string | null> {
  try {
    const { stdout } = await run('npm', ['prefix', '-g']);
    const prefix = stdout.trim();
    if (!prefix) return null;
    return `${prefix}/bin/claude`;
  } catch {
    return null;
  }
}

/**
 * Enumerate every `claude` binary visible to the host: PATH-resolved + the
 * well-known absolute slots + `~/.claude/local/claude`. Returns one entry per
 * unique resolved path with the version each reports (or null if it failed to
 * execute). Used purely for the one-shot drift warn line.
 */
export async function probeClaudeInstalls(
  run: (cmd: string, args: string[]) => Promise<{ stdout: string; stderr: string }>,
  fileExists: (path: string) => boolean,
  home: string,
): Promise<Array<{ path: string; version: string | null }>> {
  const candidates = new Set<string>(WELL_KNOWN_CLAUDE_PATHS);
  candidates.add(`${home}/.claude/local/claude`);
  // PATH-resolved. `which` is portable enough across the Linux + macOS hosts
  // we target; errors here just mean "no claude on PATH", which is fine.
  try {
    const { stdout } = await run('which', ['claude']);
    const p = stdout.trim().split('\n')[0]?.trim();
    if (p) candidates.add(p);
  } catch {
    /* no-op */
  }

  const results: Array<{ path: string; version: string | null }> = [];
  for (const p of candidates) {
    if (!fileExists(p)) continue;
    let version: string | null = null;
    try {
      const { stdout } = await run(p, ['--version']);
      version = parseVersion(stdout);
    } catch {
      /* binary exists but failed to execute — record path with null version */
    }
    results.push({ path: p, version });
  }
  return results;
}

/**
 * Bring Claude Code to the desired version if the host is idle and not already
 * on target. Safe to call every manager poll — internally throttled. Returns an
 * outcome describing what happened (for logging/observability/tests).
 */
export async function maybeUpdateClaudeCode(deps: ClaudeCodeUpdaterDeps): Promise<UpdaterOutcome> {
  const now = deps.now ? deps.now() : Date.now();
  const run = deps.runCommand ?? ((cmd, args) => execFileAsync(cmd, args, { maxBuffer: 16 * 1024 * 1024 }));
  const ps = deps.psSnapshot ?? defaultPsSnapshot;
  const fileExists = deps.fileExists ?? existsSync;
  const readClaudeJson = deps.readClaudeJson ?? defaultReadClaudeJson;
  const realPath = deps.realPath ?? realpathSync;
  const home = (deps.homeDir ?? homedir)();
  const { log } = deps;

  // 1. Host-global throttle.
  if (now - lastCheckAt < intervalMs()) {
    return { action: 'throttled' };
  }
  lastCheckAt = now;

  try {
    const spec = (deps.desiredVersion || 'latest').trim();

    // 2. ENG-5798 multi-install drift detection. Emit one warn line per
    //    process listing every claude binary + version we can see. This is
    //    independent of the install decision below — even on hosts where we
    //    proceed normally, surfacing drift is the difference between a 3-day
    //    silent loop and a one-line lead.
    if (!multiInstallWarned) {
      const installs = await probeClaudeInstalls(run, fileExists, home);
      if (installs.length > 1) {
        const summary = installs
          .map((i) => `${i.path}=${i.version ?? 'unknown'}`)
          .join(', ');
        log(`[claude-code-updater] multiple claude installs detected: ${summary}`);
      }
      multiInstallWarned = true;
    }

    // 3. ENG-5798 native-install short-circuit. When Anthropic's native
    //    autoupdater owns the host binary, the brew/npm update path is
    //    actively harmful (it installs a second binary the manager's PATH
    //    never resolves to, then verifies against yet a third). Defer.
    // ENG-10869: the path the AGENT actually reads, not the legacy one.
    // Once this host migrates, `~/.claude.json` is ARCHIVED, so a hardcoded
    // read here returns nothing forever and `configSaysNative` is permanently
    // false — silently re-opening the ENG-5798 harm this short-circuit exists
    // to prevent (brew/npm installing a second binary on a host Anthropic's
    // native autoupdater owns). The resolver falls back to the legacy file, so
    // an unmigrated host is unaffected.
    const claudeJsonPath = effectiveClaudeJsonPath(home);
    const ownership = await resolveNativeOwnership(run, realPath, readClaudeJson(claudeJsonPath));
    if (!ownership.native && ownership.configSaysNative) {
      log(
        `[claude-code-updater] ${claudeJsonPath} says installMethod=native but the binary this host runs (${ownership.binary}) is not a native install — ignoring it and converging to the pin`,
      );
    }
    if (ownership.native) {
      // One-shot info line per process — same throttle as the warn above, but
      // we re-use the throttle window to keep this from logging every poll.
      log('[claude-code-updater] native install detected, deferring to Anthropic autoupdater');
      // Reset any sticky verify-mismatch state — it can't apply once we stop
      // attempting installs.
      verifyMismatchState = null;
      return { action: 'skipped_native_install', target: spec };
    }

    // 4. Resolve the target to a concrete version (read-only). A concrete spec
    //    is its own target; a dist-tag is resolved via `npm view`.
    let target: string;
    if (isConcreteVersion(spec)) {
      target = spec;
    } else {
      const { stdout } = await run('npm', ['view', `${NPM_PKG}@${spec}`, 'version']);
      const resolved = parseVersion(stdout);
      if (!resolved) {
        log(`[claude-code-updater] could not resolve ${NPM_PKG}@${spec} to a version; skipping`);
        return { action: 'failed', reason: 'unresolved_target' };
      }
      target = resolved;
    }

    // 5. Determine the installed version (prefer the caller's cached value).
    let current = deps.currentVersion ?? null;
    if (!current) {
      try {
        const { stdout } = await run('claude', ['--version']);
        current = parseVersion(stdout);
      } catch {
        current = null; // claude missing/broken — treat as "needs install".
      }
    }

    // 6. Already on target → nothing to do.
    if (current && current === target) {
      // Successful no-op — clear sticky verify mismatch state for this target.
      if (verifyMismatchState && verifyMismatchState.target === target) {
        verifyMismatchState = null;
      }
      return { action: 'up_to_date', version: target };
    }

    // 7. Idle gate — never replace the binary under a live agent session.
    if (hostHasLiveAgent(ps())) {
      log(`[claude-code-updater] host busy (live agent) — deferring update to ${target} to a later cycle`);
      return { action: 'deferred_busy', target };
    }

    // 8. Install the target, then verify against the *absolute* path of the
    //    binary npm just wrote — not via PATH. PATH resolution is the original
    //    ENG-5798 bug: on hosts with multiple claude installs, PATH lands on a
    //    different binary than the one npm overwrites, so verify never agrees.
    const installTargetPath = await resolveInstallTargetPath(run);
    log(`[claude-code-updater] updating Claude Code ${current ?? '(none)'} → ${target}`);
    await run('npm', ['install', '-g', `${NPM_PKG}@${target}`]);

    // Prefer the absolute install target; fall back to PATH only when
    // `npm prefix -g` failed (in which case verify is best-effort).
    const verifyTarget = installTargetPath ?? 'claude';
    let installed: string | null = null;
    try {
      const { stdout } = await run(verifyTarget, ['--version']);
      installed = parseVersion(stdout);
    } catch {
      installed = null;
    }

    if (installed !== target) {
      log(
        `[claude-code-updater] post-install verify mismatch: expected ${target}, got ${installed ?? 'unknown'} from ${verifyTarget}`,
      );

      // ENG-5798: hard-error after N consecutive mismatches against the same
      // target. Reset the counter when the target changes — a new pin is a
      // new event, not a continuation of the old stuck run.
      if (!verifyMismatchState || verifyMismatchState.target !== target) {
        verifyMismatchState = { target, count: 1, alarmed: false };
      } else {
        verifyMismatchState.count += 1;
      }

      const threshold = verifyMismatchThreshold();
      if (verifyMismatchState.count >= threshold && !verifyMismatchState.alarmed) {
        verifyMismatchState.alarmed = true;
        log(
          `[claude-code-updater] ERROR verify_mismatch_stuck: ${verifyMismatchState.count} consecutive verify failures for target ${target} (verify path: ${verifyTarget}) — install is not taking effect, investigate host install slots`,
        );
        return { action: 'failed', reason: 'verify_mismatch_threshold' };
      }
      return { action: 'failed', reason: 'verify_mismatch' };
    }

    // Successful install — clear sticky state.
    verifyMismatchState = null;
    log(`[claude-code-updater] updated Claude Code ${current ?? '(none)'} → ${installed}`);
    return { action: 'updated', from: current, to: installed };
  } catch (err) {
    // Log-and-continue: an updater failure must never break the manager tick.
    log(`[claude-code-updater] update attempt failed: ${err instanceof Error ? err.message : String(err)}`);
    return { action: 'failed', reason: 'exception' };
  }
}
